// ============================================
// FILE: apiSetup/axiosInterceptor.ts
// ============================================
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import { API_BASE_URL } from '../../apiSetup/apiConfig';

const COOKIE_OPTIONS = {
  httpOnly: false,
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'Lax' as const,
  path: '/',
} as const;

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const isTokenExpired = (token: string): boolean => {
  try {
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) return true;

    const payload = JSON.parse(atob(tokenParts[1]));
    if (!payload.exp) return true;

    const expiryTime = payload.exp * 1000;
    const currentTime = Date.now();
    return currentTime >= (expiryTime - 10000);
  } catch (e) {
    console.error('Failed to decode token:', e);
    return true;
  }
};

const refreshAccessToken = async (): Promise<string> => {
  const refreshTokenValue = Cookies.get('refreshToken');

  if (!refreshTokenValue) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await axios.post(`${API_BASE_URL}api/refresh-v2`, {
      refresh_token: refreshTokenValue,
    });

    const { status, message, token, refresh_token } = response.data;

    if (status === 'error') {
      throw new Error(message || 'Token refresh failed');
    }

    if (!token || !refresh_token || typeof token !== 'string' || typeof refresh_token !== 'string') {
      throw new Error('Invalid refresh response: missing or malformed tokens');
    }

    Cookies.set('accessToken', token, COOKIE_OPTIONS);
    Cookies.set('refreshToken', refresh_token, COOKIE_OPTIONS);

    return token;
  } catch (error: any) {
    if (error.response?.data?.status === 'error') {
      throw new Error(error.response.data.message || 'Token refresh failed');
    }
    throw error;
  }
};

// Request Interceptor
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const accessToken = Cookies.get('accessToken');

    if (accessToken) {
      if (isTokenExpired(accessToken)) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            if (config.headers) {
              config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
          });
        }

        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();
          processQueue(null, newToken);

          if (config.headers) {
            config.headers.Authorization = `Bearer ${newToken}`;
          }
        } catch (error) {
          processQueue(error as AxiosError, null);
          Cookies.remove('accessToken', COOKIE_OPTIONS);
          Cookies.remove('refreshToken', COOKIE_OPTIONS);
          window.location.href = '/login';
          throw error;
        } finally {
          isRefreshing = false;
        }
      } else {
        if (config.headers) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
      }
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    } else if (config.headers && !config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ✅ FIXED Response Interceptor
apiClient.interceptors.response.use(
  (response) => {
    const data: any = response.data;

    // ✅ If response has status === 'error', reject the promise
    if (data?.status === 'error') {
      const error: any = new Error(data.message || data.details || 'Request failed');
      error.response = response;
      error.response.status = response.status || 400;
      
      // ✅ Log the error for debugging
      console.error('🔴 API Error Response:', {
        status: response.status,
        message: data.message,
        details: data.details,
        data: data
      });
      
      return Promise.reject(error);
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // ✅ Only handle 401 for token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        processQueue(null, newToken);
        return apiClient(originalRequest);
      } catch (refreshError: any) {
        processQueue(refreshError as AxiosError, null);
        Cookies.remove('accessToken', COOKIE_OPTIONS);
        Cookies.remove('refreshToken', COOKIE_OPTIONS);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;